// Safe localStorage wrapper that handles incognito mode and other edge cases
class SafeLocalStorage implements Storage {
  private memoryStorage: Map<string, string> = new Map();
  private isAvailable: boolean = false;

  constructor() {
    // Test if localStorage is available
    try {
      const testKey = '__localStorage_test__';
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(testKey, 'test');
        window.localStorage.removeItem(testKey);
        this.isAvailable = true;
      }
    } catch (e) {
      console.warn('localStorage is not available, using memory storage');
      this.isAvailable = false;
    }
  }

  get length(): number {
    if (this.isAvailable) {
      try {
        return window.localStorage.length;
      } catch (e) {
        return this.memoryStorage.size;
      }
    }
    return this.memoryStorage.size;
  }

  clear(): void {
    if (this.isAvailable) {
      try {
        window.localStorage.clear();
        return;
      } catch (e) {
        // Fall through to memory storage
      }
    }
    this.memoryStorage.clear();
  }

  getItem(key: string): string | null {
    if (this.isAvailable) {
      try {
        return window.localStorage.getItem(key);
      } catch (e) {
        // Fall through to memory storage
      }
    }
    return this.memoryStorage.get(key) || null;
  }

  key(index: number): string | null {
    if (this.isAvailable) {
      try {
        return window.localStorage.key(index);
      } catch (e) {
        // Fall through to memory storage
      }
    }
    const keys = Array.from(this.memoryStorage.keys());
    return keys[index] || null;
  }

  removeItem(key: string): void {
    if (this.isAvailable) {
      try {
        window.localStorage.removeItem(key);
        return;
      } catch (e) {
        // Fall through to memory storage
      }
    }
    this.memoryStorage.delete(key);
  }

  setItem(key: string, value: string): void {
    if (this.isAvailable) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch (e) {
        // Fall through to memory storage
      }
    }
    this.memoryStorage.set(key, value);
  }
}

// Create a singleton instance
export const safeLocalStorage = new SafeLocalStorage();

// Override global localStorage if needed
if (typeof window !== 'undefined') {
  // Check if we can access localStorage
  try {
    window.localStorage.getItem('test');
  } catch (e) {
    // Override with safe version
    Object.defineProperty(window, 'localStorage', {
      value: safeLocalStorage,
      writable: true,
      configurable: true,
    });
  }
}